import React from 'react';
import { 
  ShieldCheck, Lock, Key, AlertTriangle, ShieldAlert, 
  RotateCcw, Save, CheckCircle2, Shield
} from 'lucide-react';
import { useSecurityStore } from '../../../store/securityStore';
import { useAuthStore } from '../../../store/authStore';
import { Button } from '../../../components/Button';

interface SecurityOverviewHeaderProps {
  onSaveAll?: () => void;
  activeSubTabTitle?: string;
}

export function SecurityOverviewHeader({ onSaveAll, activeSubTabTitle }: SecurityOverviewHeaderProps) {
  const { 
    ssoProviders, mfaSettings, encryptionSettings, 
    anomalyAlerts, resetAllDefaults, hasUnsavedChanges 
  } = useSecurityStore();
  const { user } = useAuthStore();

  const isSuperAdmin = (user?.roles || []).some((r: any) => 
    typeof r === 'string' 
      ? r.toLowerCase().includes('admin') || r.toLowerCase().includes('super')
      : r.code === 'super_admin' || r.name === 'Super Admin'
  );

  const activeSsoCount = ssoProviders.filter((s) => s.status === 'active').length;
  const openAlertsCount = anomalyAlerts.filter((a) => a.status === 'open').length;
  const isDirty = Object.values(hasUnsavedChanges).some(Boolean);

  return (
    <div className="space-y-4">
      {/* Top Banner / Card */}
      <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
        <div className="flex items-start sm:items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0 mt-0.5 sm:mt-0">
            <ShieldCheck className="size-4.5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="font-display text-page-title tracking-tight text-foreground">
                Zero-Trust Security Kernel
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-3xs font-semibold uppercase tracking-wider bg-success/10 text-success-strong border border-success/30 flex items-center gap-1">
                <CheckCircle2 className="size-3" /> Shield Active
              </span>
              {!isSuperAdmin && (
                <span className="px-2.5 py-0.5 rounded-full text-3xs font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                  Read-Only Mode (Super Admin Required to Edit)
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Comprehensive Authentication, ABAC Authorization, Cryptographic Data Protection &amp; Threat Defense
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 self-start lg:self-auto">
          {isDirty && (
            <span className="text-xs font-semibold text-amber-500 animate-pulse flex items-center gap-1 mr-2">
              <AlertTriangle className="size-3.5" /> Unsaved changes
            </span>
          )}

          {isSuperAdmin && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={resetAllDefaults}
                className="gap-1.5 h-8.5 text-xs text-muted-foreground hover:text-foreground"
                title="Reset all settings to enterprise baseline"
              >
                <RotateCcw className="size-3.5" />
                <span className="hidden sm:inline">Reset Baseline</span>
              </Button>

              {onSaveAll && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={onSaveAll}
                  className="gap-1.5 h-8.5 text-xs shadow-xs"
                >
                  <Save className="size-3.5" />
                  <span>Save Changes</span>
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* KPI Security Status Chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* MFA Chip */}
        <div className="p-3.5 rounded-xl border border-border bg-card shadow-xs flex items-center gap-3">
          <div className="size-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
            <Lock className="size-4.5" />
          </div>
          <div className="min-w-0">
            <span className="text-3xs uppercase tracking-wider font-semibold text-muted-foreground block">
              Multi-Factor Auth
            </span>
            <p className="text-xs font-semibold text-foreground truncate">
              {mfaSettings.enabled ? `Enforced (${mfaSettings.requiredRoles.length} Roles)` : 'Optional'}
            </p>
          </div>
        </div>

        {/* SSO Chip */}
        <div className="p-3.5 rounded-xl border border-border bg-card shadow-xs flex items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
            <Shield className="size-4.5" />
          </div>
          <div className="min-w-0">
            <span className="text-3xs uppercase tracking-wider font-semibold text-muted-foreground block">
              Single Sign-On
            </span>
            <p className="text-xs font-semibold text-foreground truncate">
              {activeSsoCount > 0 ? `${activeSsoCount} Active IdP` : 'Local Password Only'}
            </p>
          </div>
        </div>

        {/* Encryption Chip */}
        <div className="p-3.5 rounded-xl border border-border bg-card shadow-xs flex items-center gap-3">
          <div className="size-9 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
            <Key className="size-4.5" />
          </div>
          <div className="min-w-0">
            <span className="text-3xs uppercase tracking-wider font-semibold text-muted-foreground block">
              Data Encryption
            </span>
            <p className="text-xs font-semibold text-foreground truncate">
              AES-256 / {encryptionSettings.tlsMinVersion}
            </p>
          </div>
        </div>

        {/* Threat Alerts Chip */}
        <div className="p-3.5 rounded-xl border border-border bg-card shadow-xs flex items-center gap-3">
          <div className={`size-9 rounded-lg flex items-center justify-center shrink-0 ${
            openAlertsCount > 0
              ? 'bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400'
              : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400'
          }`}>
            <ShieldAlert className="size-4.5" />
          </div>
          <div className="min-w-0">
            <span className="text-3xs uppercase tracking-wider font-semibold text-muted-foreground block">
              Threat Shield
            </span>
            <p className="text-xs font-semibold text-foreground truncate">
              {openAlertsCount > 0 ? `${openAlertsCount} Open Anomalies` : 'Zero Threat Alerts'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
